import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getSMTPConfigs, deleteSMTPConfig, setDefaultSMTPConfig, testSMTPConnection } from '../../services/smtpService';
import { SMTPConfig } from '../../types/admin';
import { SMTPConfigForm } from './SMTPConfigForm';
import { EmailTestModal } from './EmailTestModal';
import './SMTPConfigList.css';

export const SMTPConfigList: React.FC = () => {
	const { token } = useAuth();
	const [configs, setConfigs] = useState<SMTPConfig[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string>('');
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [showEditModal, setShowEditModal] = useState(false);
	const [showEmailTestModal, setShowEmailTestModal] = useState(false);
	const [selectedConfig, setSelectedConfig] = useState<SMTPConfig | null>(null);
	const [testingConnections, setTestingConnections] = useState<Record<number, boolean>>({});
	const [connectionResults, setConnectionResults] = useState<Record<number, { success: boolean; message: string } | null>>({});

	const loadConfigs = useCallback(async () => {
		if (!token) return;

		try {
			setLoading(true);
			setError('');
			const data = await getSMTPConfigs(token);
			setConfigs(data);
		} catch (err) {
			setError('Neuspešno učitavanje SMTP konfiguracija');
			console.error('SMTP configs loading error:', err);
		} finally {
			setLoading(false);
		}
	}, [token]);

	useEffect(() => {
		loadConfigs();
	}, [loadConfigs]);

	const handleCreateConfig = () => {
		setSelectedConfig(null);
		setShowCreateModal(true);
	};

	const handleEditConfig = (config: SMTPConfig) => {
		setSelectedConfig(config);
		setShowEditModal(true);
	};

	const handleDeleteConfig = async (configId: number, configName: string) => {
		if (!token) return;

		if (window.confirm(`Da li ste sigurni da želite da obrišete SMTP konfiguraciju "${configName}"?`)) {
			try {
				await deleteSMTPConfig(token, configId);
				loadConfigs();
				setError('');
			} catch (err) {
				setError('Neuspešno brisanje SMTP konfiguracije');
				console.error('Delete config error:', err);
			}
		}
	};

	const handleSetDefault = async (configId: number, configName: string) => {
		if (!token) return;

		if (window.confirm(`Postaviti "${configName}" kao podrazumevanu SMTP konfiguraciju?`)) {
			try {
				await setDefaultSMTPConfig(token, configId);
				loadConfigs();
				setError('');
			} catch (err) {
				setError('Neuspešno postavljanje podrazumevane SMTP konfiguracije');
				console.error('Set default error:', err);
			}
		}
	};

	const handleTestConnection = async (configId: number) => {
		if (!token) return;

		setTestingConnections((prev) => ({ ...prev, [configId]: true }));
		setConnectionResults((prev) => ({ ...prev, [configId]: null }));

		try {
			const result = await testSMTPConnection(token, configId);
			setConnectionResults((prev) => ({
				...prev,
				[configId]: {
					success: result.success,
					message: result.success ? 'Konekcija uspešna!' : result.error || 'Konekcija neuspešna',
				},
			}));
		} catch (err) {
			setConnectionResults((prev) => ({
				...prev,
				[configId]: {
					success: false,
					message: err instanceof Error ? err.message : 'Test konekcije neuspešan',
				},
			}));
		} finally {
			setTestingConnections((prev) => ({ ...prev, [configId]: false }));
		}
	};

	const handleEmailTest = (config: SMTPConfig) => {
		setSelectedConfig(config);
		setShowEmailTestModal(true);
	};

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString('en-US', {
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
		});
	};

	if (loading && configs.length === 0) {
		return (
			<div className='smtp-list-loading'>
				<p>Učitavanje SMTP konfiguracija...</p>
			</div>
		);
	}

	return (
		<div className='smtp-config-list'>
			<div className='smtp-header'>
				<h2>Upravljanje SMTP konfiguracijama</h2>
				<p>Konfigurišite i upravljajte podešavanjima email servera za slanje obaveštenja</p>
			</div>

			{error && <div className='error-message'>{error}</div>}

			{/* Actions */}
			<div className='smtp-actions'>
				<button onClick={handleCreateConfig} className='btn btn-primary'>
					+ Dodaj SMTP konfiguraciju
				</button>
			</div>

			{/* SMTP Configurations List */}
			{configs.length === 0 ? (
				<div className='no-configs'>
					<div className='no-configs-content'>
						<h3>Nema SMTP konfiguracija</h3>
						<p>Još uvek niste konfigurirali nijedan SMTP server. Dodajte vašu prvu konfiguraciju da biste počeli sa slanjem email-ova.</p>
						<button onClick={handleCreateConfig} className='btn btn-primary'>
							Dodaj prvu konfiguraciju
						</button>
					</div>
				</div>
			) : (
				<div className='configs-grid'>
					{configs.map((config) => (
						<div key={config.id} className={`config-card ${config.is_default ? 'default' : ''} ${!config.is_active ? 'inactive' : ''}`}>
							{/* Card Header */}
							<div className='config-card-header'>
								<div className='config-title'>
									<h3>{config.name}</h3>
									<div className='config-badges'>
										{config.is_default && <span className='badge badge-primary'>Podrazumevano</span>}
										{!config.is_active && <span className='badge badge-secondary'>Neaktivno</span>}
										{config.secure && <span className='badge badge-success'>SSL</span>}
										{config.require_tls && <span className='badge badge-info'>TLS</span>}
									</div>
								</div>
								<div className='config-actions'>
									<button onClick={() => handleEditConfig(config)} className='action-btn edit-btn' title='Uredi konfiguraciju'>
										✏️
									</button>
									<button onClick={() => handleDeleteConfig(config.id, config.name)} className='action-btn delete-btn' title='Obriši konfiguraciju'>
										🗑️
									</button>
								</div>
							</div>

							{/* Card Body */}
							<div className='config-card-body'>
								<div className='config-info'>
									<div className='info-group'>
										<span className='label'>Host:</span>
										<span className='value'>
											{config.host}:{config.port}
										</span>
									</div>
									<div className='info-group'>
										<span className='label'>Korisničko ime:</span>
										<span className='value'>{config.username}</span>
									</div>
									<div className='info-group'>
										<span className='label'>Email pošaljioce:</span>
										<span className='value'>{config.from_email}</span>
									</div>
									{config.from_name && (
										<div className='info-group'>
											<span className='label'>Ime pošaljioce:</span>
											<span className='value'>{config.from_name}</span>
										</div>
									)}
									<div className='info-group'>
										<span className='label'>Kreiran:</span>
										<span className='value'>{formatDate(config.created_at)}</span>
									</div>
								</div>

								{/* Connection Test Result */}
								{connectionResults[config.id] && (
									<div className={`connection-result ${connectionResults[config.id]!.success ? 'success' : 'error'}`}>
										<span className={`status-icon ${connectionResults[config.id]!.success ? 'success' : 'error'}`}>{connectionResults[config.id]!.success ? '✓' : '✗'}</span>
										{connectionResults[config.id]!.message}
									</div>
								)}
							</div>

							{/* Card Footer */}
							<div className='config-card-footer'>
								<div className='footer-actions'>
									<button onClick={() => handleTestConnection(config.id)} disabled={testingConnections[config.id]} className='btn btn-test'>
										{testingConnections[config.id] ? 'Testiranje...' : 'Testiraj konekciju'}
									</button>

									<button onClick={() => handleEmailTest(config)} disabled={!config.is_active} className='btn btn-secondary'>
										Pošalji test email
									</button>

									{!config.is_default && config.is_active && (
										<button onClick={() => handleSetDefault(config.id, config.name)} className='btn btn-outline'>
											Postavi kao podrazumevanu
										</button>
									)}
								</div>
							</div>
						</div>
					))}
				</div>
			)}

			{/* Modals */}
			{showCreateModal && (
				<SMTPConfigForm
					isOpen={showCreateModal}
					onClose={() => setShowCreateModal(false)}
					onSuccess={() => {
						setShowCreateModal(false);
						loadConfigs();
					}}
					mode='create'
				/>
			)}

			{showEditModal && selectedConfig && (
				<SMTPConfigForm
					config={selectedConfig}
					isOpen={showEditModal}
					onClose={() => setShowEditModal(false)}
					onSuccess={() => {
						setShowEditModal(false);
						loadConfigs();
					}}
					mode='edit'
				/>
			)}

			{showEmailTestModal && selectedConfig && <EmailTestModal config={selectedConfig} isOpen={showEmailTestModal} onClose={() => setShowEmailTestModal(false)} />}
		</div>
	);
};
